import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaExclamationTriangle, FaCheckCircle, FaPaperclip, FaTimes, FaSpinner } from "react-icons/fa";
import { API_CONFIG, buildApiUrl } from "../../common/api.js";
import logo from "../assets/logo.png";

const CATEGORIE = [
  { value: "frode_truffa",            label: "Frode / Truffa" },
  { value: "prodotto_non_conforme",   label: "Prodotto non conforme" },
  { value: "comportamento_scorretto", label: "Comportamento scorretto" },
  { value: "spedizione",              label: "Problema spedizione" },
  { value: "qualita_prodotto",        label: "Qualità prodotto" },
  { value: "sicurezza_alimentare",    label: "Sicurezza alimentare" },
  { value: "privacy_dati",            label: "Privacy / Dati personali" },
  { value: "altro",                   label: "Altro" },
];

const MAX_FILES     = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MIME_ACCETTATI = ["image/jpeg", "image/png", "image/gif", "application/pdf",
  "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/zip", "text/plain"];

export default function Segnalazione() {
  const [form, setForm] = useState({
    nome:        "",
    email:       "",
    telefono:    "",
    categoria:   "",
    oggetto:     "",
    descrizione: "",
  });
  const [allegati, setAllegati]       = useState([]);
  const [errors, setErrors]           = useState({});
  const [loading, setLoading]         = useState(false);
  const [successo, setSuccesso]       = useState(false);
  const [tokenAccesso, setTokenAccesso] = useState("");
  const [erroreServer, setErroreServer] = useState("");
  const fileRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const nuovi = Array.from(e.target.files);
    const fileErrors = [];
    const validi = [];

    for (const f of nuovi) {
      if (allegati.length + validi.length >= MAX_FILES) {
        fileErrors.push(`Massimo ${MAX_FILES} allegati consentiti.`);
        break;
      }
      if (f.size > MAX_FILE_SIZE) {
        fileErrors.push(`"${f.name}" supera i 10 MB.`);
        continue;
      }
      if (!MIME_ACCETTATI.includes(f.type)) {
        fileErrors.push(`"${f.name}" ha un formato non supportato.`);
        continue;
      }
      validi.push(f);
    }

    if (fileErrors.length > 0) {
      setErrors((prev) => ({ ...prev, allegati: fileErrors.join(" ") }));
    } else {
      setErrors((prev) => ({ ...prev, allegati: "" }));
    }

    setAllegati((prev) => [...prev, ...validi]);
    e.target.value = "";
  };

  const rimuoviAllegato = (index) => {
    setAllegati((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => ({ ...prev, allegati: "" }));
  };

  const valida = () => {
    const nuovi = {};
    if (!form.categoria)   nuovi.categoria   = "Seleziona una categoria.";
    if (!form.oggetto.trim())     nuovi.oggetto     = "L'oggetto è obbligatorio.";
    if (!form.descrizione.trim()) nuovi.descrizione = "La descrizione è obbligatoria.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      nuovi.email = "Inserisci un'email valida.";
    }
    return nuovi;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErroreServer("");

    const nuoviErrori = valida();
    if (Object.keys(nuoviErrori).length > 0) {
      setErrors(nuoviErrori);
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) formData.append(k, v); });
      allegati.forEach((f) => formData.append("allegati[]", f));

      const url = buildApiUrl(API_CONFIG.ENDPOINTS.SEGNALAZIONI_PUBBLICHE);
      const response = await fetch(url, {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          const mapped = {};
          Object.entries(data.errors).forEach(([k, v]) => { mapped[k.replace("allegati.0", "allegati")] = Array.isArray(v) ? v[0] : v; });
          setErrors(mapped);
        } else {
          setErroreServer(data.message || "Errore durante l'invio.");
        }
        return;
      }

      setSuccesso(true);
      setTokenAccesso(data.token_accesso || "");

    } catch {
      setErroreServer("Errore di rete. Verifica la connessione e riprova.");
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  if (successo) {
    return (
      <div className="min-h-screen bg-[#FFFEFA] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center"
        >
          <FaCheckCircle className="text-green-500 text-5xl mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#c78244] mb-3">Segnalazione inviata!</h2>
          <p className="text-gray-600 mb-4">
            Grazie per il tuo contributo. Il team Mangitalia analizzerà la segnalazione e ti ricontatterà se hai fornito un'email.
          </p>
          {tokenAccesso && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
              <p className="text-sm text-amber-800 font-semibold mb-1">Codice di riferimento:</p>
              <p className="font-mono text-xs break-all text-amber-900">{tokenAccesso}</p>
              <p className="text-xs text-amber-600 mt-1">Conserva questo codice per seguire la tua segnalazione.</p>
            </div>
          )}
          <button
            onClick={() => window.location.href = `/segnalazione/stato/${tokenAccesso}`}
            className="bg-gradient-to-r from-[#F6CF48] to-[#c78244] text-white px-6 py-2 rounded-full font-bold hover:scale-105 transition-all mr-2"
          >
            Traccia segnalazione
          </button>
          <button
            onClick={() => window.history.back()}
            className="text-[#c78244] underline px-4 py-2"
          >
            Torna alla home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFEFA]">
      <div className="w-full bg-gradient-to-r from-[#F6CF48] to-[#c78244] py-10 px-4 text-white text-center">
        <img src={logo} alt="Mangitalia" className="w-20 mx-auto mb-3 opacity-90" />
        <h1 className="text-3xl font-bold mb-2">Canale di Segnalazione</h1>
        <p className="text-sm opacity-90 max-w-xl mx-auto">
          Segnala in modo anonimo o identificato qualsiasi irregolarità, problema o comportamento scorretto.
          Ogni segnalazione viene gestita con riservatezza dal team Mangitalia.
        </p>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border-l-4 border-amber-400 rounded-xl p-4 mb-8 flex gap-3"
        >
          <FaExclamationTriangle className="text-amber-500 text-xl flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Segnalazione anonima supportata.</strong> Nome, email e telefono sono facoltativi.
            Se fornisci un'email potrai essere ricontattato per aggiornamenti sulla tua segnalazione.
          </div>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl shadow-lg p-8 space-y-6"
          noValidate
        >
          <div>
            <h3 className="font-bold text-[#c78244] text-lg mb-4">Dati mittente <span className="font-normal text-sm text-gray-400">(facoltativi)</span></h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input type="text" name="nome" value={form.nome} onChange={handleChange} placeholder="Il tuo nome"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F6CF48]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                <input type="tel" name="telefono" value={form.telefono} onChange={handleChange} placeholder="+39 ..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F6CF48]" />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange}
                placeholder="Per ricevere aggiornamenti sulla segnalazione"
                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F6CF48] ${errors.email ? "border-red-400" : "border-gray-200"}`} />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
          </div>

          <hr className="border-gray-100" />

          <div>
            <h3 className="font-bold text-[#c78244] text-lg mb-4">Dettagli segnalazione</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoria <span className="text-red-500">*</span></label>
              <select name="categoria" value={form.categoria} onChange={handleChange}
                className={`w-full border rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-offset-2 focus:ring-[#F6CF48] ${errors.categoria ? "border-red-400" : "border-gray-200"}`}>
                <option value="">— Seleziona una categoria —</option>
                {CATEGORIE.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              {errors.categoria && <p className="text-red-500 text-xs mt-1">{errors.categoria}</p>}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Oggetto <span className="text-red-500">*</span></label>
              <input type="text" name="oggetto" value={form.oggetto} onChange={handleChange}
                placeholder="Breve titolo della segnalazione" maxLength={255}
                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F6CF48] ${errors.oggetto ? "border-red-400" : "border-gray-200"}`} />
              {errors.oggetto && <p className="text-red-500 text-xs mt-1">{errors.oggetto}</p>}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione <span className="text-red-500">*</span></label>
              <textarea name="descrizione" value={form.descrizione} onChange={handleChange} rows={5} maxLength={5000}
                placeholder="Descrivi il problema nel dettaglio..."
                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F6CF48] resize-none ${errors.descrizione ? "border-red-400" : "border-gray-200"}`} />
              <div className="flex justify-between">
                {errors.descrizione ? <p className="text-red-500 text-xs mt-1">{errors.descrizione}</p> : <span />}
                <p className="text-xs text-gray-400 mt-1">{form.descrizione.length}/5000</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allegati <span className="text-gray-400 font-normal">(max {MAX_FILES} file, 10 MB ciascuno)</span>
              </label>
              <div onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-[#F6CF48] transition-colors">
                <FaPaperclip className="text-gray-300 text-2xl mx-auto mb-1" />
                <p className="text-xs text-gray-400">Clicca per allegare file (immagini, PDF, documenti)</p>
                <input ref={fileRef} type="file" multiple
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.zip,.txt"
                  onChange={handleFileChange} className="hidden" disabled={allegati.length >= MAX_FILES} />
              </div>
              {errors.allegati && <p className="text-red-500 text-xs mt-1">{errors.allegati}</p>}

              <AnimatePresence>
                {allegati.map((f, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}
                    className="flex items-center justify-between bg-amber-50 rounded-xl px-4 py-2 mt-2 text-sm">
                    <span className="text-gray-700 truncate max-w-xs">{f.name}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-gray-400 text-xs">{formatBytes(f.size)}</span>
                      <button type="button" onClick={() => rimuoviAllegato(i)} className="text-red-400 hover:text-red-600 transition-colors">
                        <FaTimes />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {erroreServer && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">{erroreServer}</div>
          )}

          <button type="submit" disabled={loading}
            className="w-full bg-gradient-to-r from-[#F6CF48] to-[#c78244] text-white py-3 rounded-xl font-bold text-base hover:scale-105 transition-all shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {loading ? (<><FaSpinner className="animate-spin" /> Invio in corso...</>) : "Invia segnalazione"}
          </button>
        </motion.form>
      </div>
    </div>
  );
}
